/* eslint-disable unicorn/filename-case -- TODO rename later */
import React from "react";

import { LoadingSpinner } from "~/assets/svg";
import { cn } from "~/utils";

import { Tooltip, TooltipContent, TooltipTrigger } from "../Tooltip/Tooltip";

export type ButtonVariant =
  | "default"
  | "primary"
  | "secondary"
  | "ternary"
  | "outlined"
  | "transparent"
  | "danger"
  | "warning";

type ButtonProps = {
  className?: string;
  dataTestId?: string;
  disabled?: boolean;
  endIcon?: React.ReactNode;
  fullWidth?: boolean;
  tooltip?: string;
  label: string;
  loading?: boolean;
  small?: boolean;
  startIcon?: React.ReactNode;
  variant: ButtonVariant;
  onClick: () => void;
};

/**
 * @deprecated Use `~/components/ui/button` instead.
 *
 * @example
 * import { Button } from "~/components/ui/button"
 */
const Button = ({
  className = "",
  dataTestId,
  disabled = false,
  endIcon,
  fullWidth,
  tooltip = "",
  label,
  loading = false,
  small = false,
  startIcon,
  variant = "default",
  onClick,
}: ButtonProps) => {
  const buttonStyleBase = `flex items-center max-w-full gap-x-2 px-3 rounded whitespace-pre truncate transition-all duration-150 disabled:opacity-50 font-semibold ${endIcon ? "justify-between" : "justify-center"}`;
  const buttonStyle = `${buttonStyleBase} py-2`;
  const buttonHeight = small ? "h-8" : "h-10";
  const fullWidthStyle = fullWidth ? "w-full" : "";
  const textSize = small ? "text-xs" : "text-sm";
  const variantStyles = {
    default:
      "border border-gray-950 text-white bg-gray-800 enabled:hover:bg-gray-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)] enabled:hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.15)] enabled:hover:border-gray-900",
    primary:
      "border border-primary-600 text-white bg-primary-600 enabled:hover:bg-primary-700 enabled:hover:border-primary-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] enabled:hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.3)]",
    secondary:
      "border border-gray-200 bg-gray-100 text-primary-600 enabled:hover:border-gray-300 enabled:hover:text-primary-700 enabled:hover:bg-gray-200",
    ternary:
      "border border-gray-200 text-gray-600 bg-gray-100 enabled:hover:bg-gray-200 enabled:hover:border-gray-300 enabled:hover:text-gray-700",
    outlined: "border border-gray-300 text-gray-600 bg-white enabled:hover:border-gray-400 enabled:hover:bg-gray-50",
    transparent:
      "border border-transparent text-primary-600 bg-transparent enabled:hover:bg-primary-50 enabled:hover:text-primary-700",
    danger:
      "border border-red-500 text-white bg-red-500 enabled:hover:bg-red-600 enabled:hover:border-red-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.3)]",
    warning: "border border-red-200 text-red-700 bg-red-50 enabled:hover:bg-red-100 enabled:hover:border-red-300",
  };

  const tooltipBtnComponent = (
    <span className="flex items-center justify-center gap-x-2" data-testid={dataTestId}>
      {loading ? (
        <LoadingSpinner data-testid="button-inner-loading-spinner" width="16px" height="16px" />
      ) : (
        startIcon || null
      )}
      {label}
      {endIcon && endIcon}
    </span>
  );

  if (tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger
          disabled={disabled || loading}
          className={cn(buttonStyle, fullWidthStyle, buttonHeight, textSize, variantStyles[variant], className)}
          onClick={onClick}
        >
          {tooltipBtnComponent}
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    );
  }
  return (
    <button
      className={cn(buttonStyle, fullWidthStyle, buttonHeight, textSize, variantStyles[variant], className)}
      data-testid={dataTestId}
      disabled={disabled || loading}
      type="button"
      onClick={onClick}
    >
      {loading ? (
        <LoadingSpinner data-testid="button-inner-loading-spinner" width="16px" height="16px" />
      ) : (
        startIcon || null
      )}
      {label}
      {endIcon && endIcon}
    </button>
  );
};

export default Button;
